import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'framer-motion';

import type { AdminStatsResponse } from '@delegation/contracts';

import { useAdminStatsQuery } from '@/features/admin/api';
import { useMeQuery } from '@/features/auth/api';
import { getErrorMessage } from '@/shared/api/http';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  PageHeader,
  Skeleton,
  Stat,
} from '@/shared/components/ui';
import { PageTransition, staggerContainer, staggerItem } from '@/shared/motion';

const STATS: {
  key: keyof AdminStatsResponse;
  label: string;
  tone: 'warning' | 'info' | 'success' | 'default';
  hint: string;
}[] = [
  {
    key: 'pendingSubmissions',
    label: 'Pending submissions',
    tone: 'warning',
    hint: 'Awaiting review',
  },
  {
    key: 'delegatedUsers',
    label: 'Delegated users',
    tone: 'info',
    hint: 'Login-capable tenants',
  },
  { key: 'workers', label: 'Workers', tone: 'success', hint: 'Maids, cleaners, maintenance' },
  {
    key: 'auditEvents24h',
    label: 'Audit events (24h)',
    tone: 'default',
    hint: 'Admin actions · 24h',
  },
];

export const Route = createFileRoute('/admin/')({
  component: AdminHome,
});

function AdminHome() {
  const me = useMeQuery();
  const stats = useAdminStatsQuery();

  return (
    <PageTransition className="flex flex-col gap-8">
      <PageHeader
        title="Overview"
        description="Access requests and the people behind them. Live counts land alongside Phase 9."
      />

      {me.error && (
        <Alert tone="danger" title="Couldn't load your session">
          {getErrorMessage(me.error)}
        </Alert>
      )}

      {/* Stat row — placeholders until live counts wire up (Phase 3). ----- */}
      <motion.section
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        {STATS.map((s) => (
          <motion.div key={s.label} variants={staggerItem}>
            <Stat
              label={s.label}
              value="—"
              valueNumber={stats.data?.[s.key]}
              pending={stats.isPending}
              tone={s.tone}
              hint={s.hint}
            />
          </motion.div>
        ))}
      </motion.section>

      {/* Account card --------------------------------------------------- */}
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Account"
            description="Your current session — change password under settings (Phase 9)."
          />
          <CardContent>
            {me.isPending && (
              <div className="grid grid-cols-2 gap-y-4">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-48" />
              </div>
            )}
            {me.data?.admin && (
              <dl className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-[max-content_1fr]">
                <dt className="text-foreground-muted">Name</dt>
                <dd className="font-medium">{me.data.admin.fullName}</dd>
                <dt className="text-foreground-muted">Email</dt>
                <dd className="font-medium">{me.data.admin.email}</dd>
                <dt className="text-foreground-muted">Role</dt>
                <dd>
                  <span className="rounded-md bg-surface-muted px-2 py-0.5 font-mono text-xs">
                    {me.data.admin.role}
                  </span>
                </dd>
                <dt className="text-foreground-muted">Last sign-in</dt>
                <dd>
                  {me.data.admin.lastLoginAt
                    ? new Date(me.data.admin.lastLoginAt).toLocaleString()
                    : '—'}
                </dd>
              </dl>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Getting started" description="Common admin tasks." />
          <CardContent>
            <ul className="flex flex-col gap-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-[image:var(--gradient-brand)] text-xs font-semibold text-brand-foreground shadow-glow-brand">
                  1
                </span>
                <span>
                  Review pending <span className="font-medium">Submissions</span> and approve or
                  reject them.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-[image:var(--gradient-brand)] text-xs font-semibold text-brand-foreground shadow-glow-brand">
                  2
                </span>
                <span>
                  Add or block <span className="font-medium">Delegated users</span> and{' '}
                  <span className="font-medium">Workers</span> directly.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-[image:var(--gradient-brand)] text-xs font-semibold text-brand-foreground shadow-glow-brand">
                  3
                </span>
                <span>
                  Trace every state change in the <span className="font-medium">Audit log</span>.
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </section>
    </PageTransition>
  );
}
